/**
 * Étapes des Fêtes internationales de saint Léonard — 2027.
 *
 * Chaque étape locale devient une mini-page autonome accessible via
 * `/fetes-internationales/:slug`. Le format est optimisé pour l'ajout
 * incrémental : il suffit d'ajouter une entrée à cette liste pour créer
 * automatiquement l'entrée dans l'index et sa page dédiée.
 *
 * Structure d'un objet :
 *   slug              — identifiant URL (kebab-case)
 *   country           — pays (nom en français)
 *   flag              — emoji drapeau
 *   city              — ville(s)
 *   dates             — texte libre lisible ("avril 2027", "10-14 juillet 2027")
 *   sort_date         — ISO YYYY-MM-DD utilisé pour le tri chronologique
 *   theme             — un des trois axes Libération · Lumière · Unité
 *   hero_color        — teinte du hero (palette d'enluminure)
 *   accent_color      — teinte d'accent
 *   resonance         — paragraphe évoquant le lien avec saint Léonard
 *   host              — partenaire local (paroisse, diocèse, association)
 *   coords            — [latitude, longitude] pour la future carte mondiale
 */

export const FETES_LOCATIONS = [
  {
    slug: "egypte-mansourah",
    country: "Égypte",
    flag: "🇪🇬",
    city: "Mansourah",
    dates: "Avril 2027",
    sort_date: "2027-04-15",
    theme: "Libération",
    hero_color: "#c8a26b",     // sable du désert
    accent_color: "#7b1f2b",   // cinabre
    resonance:
      "Mansourah — « la victorieuse » en arabe — porte encore la mémoire de la septième croisade et de la captivité de Saint Louis en 1250. Terre de libération et de rencontre entre Orient et Occident, elle accueille la première étape des Fêtes internationales de saint Léonard, patron des prisonniers et des libérés.",
    host: "Paroisse copte de Mansourah",
    coords: [31.0409, 31.3785],
  },
  {
    slug: "israel-bethleem-jerusalem",
    country: "Israël & Territoires palestiniens",
    flag: "🇮🇱",
    city: "Bethléem & Jérusalem",
    dates: "Décembre 2027",
    sort_date: "2027-12-15",
    theme: "Lumière",
    hero_color: "#e6d9a2",     // pierre de Jérusalem
    accent_color: "#c1272d",
    resonance:
      "De la basilique de la Nativité à Bethléem jusqu'aux murs de la Vieille Ville de Jérusalem, l'étape de la Nativité célèbre la Lumière née dans la grotte. Elle réunit chrétiens d'Orient et d'Occident autour du saint qui affranchit — à la charnière des trois monothéismes.",
    host: "Custodie de Terre Sainte",
    coords: [31.7050, 35.2024],
  },
  {
    slug: "russie-saint-petersbourg",
    country: "Russie",
    flag: "🇷🇺",
    city: "Saint-Pétersbourg",
    dates: "Mai 2027",
    sort_date: "2027-05-15",
    theme: "Unité",
    hero_color: "#5b7a9c",     // gris Neva
    accent_color: "#d4af37",
    resonance:
      "Ville des tsars et des icônes, capitale culturelle de la Russie, Saint-Pétersbourg accueille l'étape orientale des Fêtes. Elle rappelle qu'avant les schismes, la chrétienté était une — et que la mémoire de saint Léonard (Sviatoï Leonard) est vive dans le monde orthodoxe.",
    host: "Cathédrale Saint-Isaac & communautés francophones",
    coords: [59.9311, 30.3609],
  },
  {
    slug: "angleterre-norwich",
    country: "Angleterre",
    flag: "🇬🇧",
    city: "Norwich",
    dates: "10 → 14 juillet 2027",
    sort_date: "2027-07-10",
    theme: "Lumière",
    hero_color: "#9db487",     // vert East Anglia
    accent_color: "#7b1f2b",
    resonance:
      "Norwich, ville normande de l'East Anglia, garde le souvenir de la conquête et la chapelle Saint-Léonard fondée peu après 1066. L'étape anglaise fait écho à la parenté normande entre les deux côtés de la Manche — de Vains à Norfolk.",
    host: "Cathedral of the Holy and Undivided Trinity, Norwich",
    coords: [52.6309, 1.2974],
  },
  {
    slug: "angleterre-londres",
    country: "Angleterre",
    flag: "🇬🇧",
    city: "Londres",
    dates: "21 → 24 septembre 2027",
    sort_date: "2027-09-21",
    theme: "Unité",
    hero_color: "#4a5a6e",     // gris Tamise
    accent_color: "#d4af37",
    resonance:
      "L'étape londonienne réunit la diaspora normande et les descendants des grandes familles anglo-normandes autour de saint Léonard, dans une capitale qui fut, dès la fin du XIᵉ siècle, l'une des grandes villes du monde normand.",
    host: "St Leonard's, Shoreditch",
    coords: [51.5074, -0.1278],
  },
  {
    slug: "maroc-rabat",
    country: "Maroc",
    flag: "🇲🇦",
    city: "Rabat",
    dates: "Mai 2027",
    sort_date: "2027-05-01",
    theme: "Libération",
    hero_color: "#c76c4a",     // ocre médina
    accent_color: "#3f7a5e",   // vert Islam
    resonance:
      "Sous la Kasbah des Oudayas, entre océan et fleuve, Rabat accueille une étape dédiée au dialogue interreligieux. Saint Léonard, qui libérait les captifs sans distinction de foi, y devient signe d'une amitié possible entre Maroc, France et diaspora chrétienne d'Afrique du Nord.",
    host: "Archidiocèse de Rabat",
    coords: [34.0209, -6.8416],
  },
  {
    slug: "benin-cotonou",
    country: "Bénin",
    flag: "🇧🇯",
    city: "Cotonou",
    dates: "Juin 2027",
    sort_date: "2027-06-15",
    theme: "Unité",
    hero_color: "#c19a3e",     // or africain
    accent_color: "#7b1f2b",
    resonance:
      "Sur les côtes du golfe de Guinée, Cotonou tisse la mémoire de la traite et l'espérance de la libération. L'étape béninoise est la voix des Amériques et de l'Afrique de l'Ouest dans le grand chant du Millenium — celle des chaînes brisées, du souffle et de la joie.",
    host: "Archidiocèse de Cotonou",
    coords: [6.3654, 2.4183],
  },
];

// Sorted ascending by sort_date for the timeline view.
export const FETES_LOCATIONS_SORTED = [...FETES_LOCATIONS].sort(
  (a, b) => a.sort_date.localeCompare(b.sort_date),
);

export function findFeteBySlug(slug) {
  return FETES_LOCATIONS.find((l) => l.slug === slug);
}
